import { DCFilter, Sort } from './dbTypes'
import { DBProps, WorkspaceAndEvent } from './sharedTypes'

export interface LeadWithSessions extends Lead, WorkspaceAndEvent, DBProps {
  sessions?: Array<LeadSession & DBProps>
}

export interface SessionWithLead extends LeadSession, WorkspaceAndEvent, DBProps {
  lead?: Lead & DBProps
}

export interface DraftSession extends Partial<LeadSession> {
  lead?: Partial<Lead>
}

export interface Lead extends PrimaryLeadProperties, HelperLeadProperties {
  custom?: Record<string, any>
  smartProperties?: SmartProperty[]
}

export interface GeoLeadProperties {
  address?: string
  address2?: string
  city?: string
  /** 2-digit ISO country code */
  country?: string
  /** Google administrative_area_level_2 field value */
  county?: string
  /** Full original address from the lead before geocoding. You can also provide only this field and the geocoding will handle creating the individual address fields. */
  fullOriginalAddress?: string
  lat?: number
  lng?: number
  /** 2-digit ISO state code. Google administrative_area_level_1 field short code */
  state?: string
  zip?: string
}
export type GeoLeadProperty = keyof GeoLeadProperties
export const geoLeadProperties: GeoLeadProperty[] = [
  'address',
  'address2',
  'city',
  'country',
  'county',
  'fullOriginalAddress',
  'lat',
  'lng',
  'state',
  'zip',
]

export interface IdentityLeadProperties {
  company?: string
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  school?: string
  title?: string
}
export type IdentityLeadProperty = keyof IdentityLeadProperties
export const identityLeadProperties: IdentityLeadProperty[] = [
  'company',
  'email',
  'firstName',
  'lastName',
  'phone',
  'school',
  'title',
]

export type PrimaryLeadProperties = GeoLeadProperties & IdentityLeadProperties
export type PrimaryLeadProperty = keyof PrimaryLeadProperties
export const primaryLeadProperties: PrimaryLeadProperty[] = [
  ...geoLeadProperties,
  ...identityLeadProperties,
]

export interface HelperLeadProperties {
  /** Status of api data if used. If api data is needed (i.e. qr code), set to pending */
  apiDataStatus?: 'pending' | 'completed' | 'failed'
  /** useful for events with registration (i.e. qr code) */
  badge?: string
  consents?: LeadConsent[]
  crmSync?: 'pending' | 'synced' | 'failed'
  followUp?: boolean
  followUpGroup?: string
  /** if provided owner is the user's name, else the owner is the workspace followup group name */
  followUpUserId?: string
  geoCodingStatus?: 'completed' | 'failed'
  /** server generated - firstName + lastName or email */
  readonly name?: string
  notes?: string
  /** (deprecated) Prefer consents */
  optIn?: boolean
  processingStatus?: 'processing' | 'completed' | 'failed'
  smartPropertyFailure?: boolean
  temp?: LeadTemperature
}

/** Consent acceptance following RegistrationConsentItem shape */
export interface LeadConsent {
  /** RegistrationConsentItem title or "Consent [consent index]" */
  title: string
  accepted: boolean | null
  /** RegistrationConsentSubItem titles that are accepted */
  acceptedSubItems?: string[]
}

export type LeadTemperature = 'hot' | 'warm' | 'cold'

export const leadTemperatures: LeadTemperature[] = ['hot', 'warm', 'cold']

export type HelperLeadProperty = keyof HelperLeadProperties

export const helperLeadProperties: HelperLeadProperty[] = [
  'apiDataStatus',
  'badge',
  'crmSync',
  'consents',
  'followUp',
  'followUpGroup',
  'followUpUserId',
  'geoCodingStatus',
  'name',
  'notes',
  'optIn',
  'processingStatus',
  'smartPropertyFailure',
  'temp',
]

export const leadProperties: (keyof Lead)[] = [
  ...primaryLeadProperties,
  ...helperLeadProperties,
  'custom',
  'smartProperties',
]

export interface SmartProperty {
  name: string
  number?: number
  text?: string
}

export interface LeadSession {
  appName: 'explorer' | 'self-registration' | 'custom'
  /** the name of the app, used for custom apps */
  appTitle?: string
  deviceName?: string
  endMethod?: EndSessionMethod
  mediaStatus?: MediaStatus
  /** track interactions (including durations) of step sequences or actions */
  metrics?: SessionMetric[]
  momentId?: number
  templateId?: string
  traits?: SessionTrait[]
}

export type MediaStatus = 'pending' | 'uploaded' | 'failed'

export interface SessionTrait {
  groupTitle: string
  traitId?: string
  title: string
}

export interface SessionMetric {
  duration?: number
  /** "_default" - primary app durations steps, "_action" - action steps, or custom group title */
  groupTitle: string
  title: string
}

export type EndSessionMethod =
  | 'early-button' // user left early
  | 'final-button' // user completed experience and ended
  | 'settings' // show worker ended session
  | 'timeout-button' // session timed out and user clicked button to end
  | 'timeout-auto' // session timed out and ended automatically

export interface LeadSessionsFetchProps<PopulateLead extends boolean = false>
  extends LeadSessionFetchOptions<PopulateLead>, CommonLeadRequestProps {}

export interface LeadSessionFetchOptions<PopulateLead extends boolean = false> {
  hideLeadProperties?: (keyof Lead)[]
  hideProperties?: (keyof LeadSession)[]
  populateLead?: PopulateLead
}
export interface LeadsFetchProps<PopulateSessions extends boolean = false>
  extends LeadFetchOptions<PopulateSessions>, CommonLeadRequestProps {}

export interface LeadFetchOptions<PopulateSessions extends boolean = false> {
  hideProperties?: (keyof Lead)[]
  hideSessionProperties?: (keyof LeadSession)[]
  populateSessions?: PopulateSessions
}

export interface CommonLeadRequestProps {
  filters?: DCFilter[]
  limit?: number
  offset?: number
  search?: string
  sort?: Sort
}
